import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { services } from '../../mock';

const serviceMeta = {
  1: { image: '/image/service-soins.webp', italic: 'soigner' },
  2: { image: '/image/service-implants.webp', italic: 'restaurer' },
  3: { image: '/image/service-parodonto.webp', italic: 'protéger' },
  4: { image: '/image/service-ortho.webp', italic: 'aligner' },
  5: { image: '/image/service-aligners.webp', italic: 'révéler' },
  6: { image: '/image/service-protheses.webp', italic: 'reconstruire' },
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function ServicesV2({ scrollToSection }) {
  return (
    <section id="services" className="bg-background py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-12 gap-y-6 mb-20 lg:mb-28">
          <div className="md:col-span-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
              01 — Nos soins
            </span>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-foreground">
              Six gestes pour <span className="italic text-foreground/85">votre bouche</span>.
            </h2>
            <p className="mt-6 max-w-2xl text-base lg:text-lg text-foreground/65 leading-relaxed">
              De la consultation préventive à la chirurgie implantaire — un cabinet pluridisciplinaire,
              un seul interlocuteur, et des soins pensés à long terme.
            </p>
          </div>
        </motion.div>

        <ol className="divide-y divide-foreground/10 border-y border-foreground/10">
          {services.map((service, idx) => {
            const meta = serviceMeta[service.id];
            const reverse = idx % 2 === 1;
            return (
              <motion.li
                key={service.id}
                {...fadeUp}
                className="group grid grid-cols-1 md:grid-cols-12 gap-y-4 md:gap-x-8 py-10 lg:py-14"
              >
                <div className={`md:col-span-1 ${reverse ? 'md:order-3' : ''}`}>
                  <span className="font-display text-5xl lg:text-6xl text-foreground/30 leading-none">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className={`md:col-span-7 ${reverse ? 'md:order-1' : 'md:order-2'}`}>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-foreground/55">
                    {service.category}
                  </span>
                  <h3 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-foreground">
                    {service.title.split(' ').slice(0, -1).join(' ')}{' '}
                    <span className="italic text-foreground/80">
                      {service.title.split(' ').slice(-1)[0]}
                    </span>
                    .
                  </h3>
                  <p className="mt-5 max-w-xl text-sm sm:text-base text-foreground/65 leading-relaxed">
                    {service.description}
                  </p>
                  <button
                    type="button"
                    onClick={() => scrollToSection('contact')}
                    className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-foreground hover:text-amber-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground rounded"
                  >
                    Prendre RDV
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </button>
                </div>

                <div className={`md:col-span-4 ${reverse ? 'md:order-2' : 'md:order-3'}`}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-md ring-1 ring-foreground/10">
                    <img
                      src={meta.image}
                      alt={service.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                    />
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
